import React, { useState, useCallback } from 'react';
import { Edit2, MoreVertical, Check, AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Id } from '@hero/schema';
import {
  Button,
  SavedLocationDisplay,
  ClientDisplay,
  ContactsTooltipDisplay,
} from '@hero/web-components';
import { MapLocationData } from '@hero/web-components';
import { useMapContext } from '@hero/web-context';
import { LocationAccuracyModal } from '../LocationAccuracyModal';
import { LocationAddIcon, PencilIcon } from '@hero/web-icons';

export interface LocationCardProps {
  /** Location data */
  location: MapLocationData;
  /** Index of the location */
  index: number;
  /** Whether this is the origin/pickup location */
  isOrigin: boolean;
  /** Callback when card is clicked (for smooth zoom) */
  onCardClick?: () => void;
  /** Callback when edit button is clicked */
  onEdit?: () => void;
  /** Callback when done button is clicked (to finish editing) */
  onDone?: () => void;
  /** Callback when cancel button is clicked (to cancel editing) */
  onCancel?: () => void;
  /** Callback when save location button is clicked */
  onSaveLocation?: () => void;
  /** Callback when "Edit saved location" is clicked (opens saved location form in edit mode). */
  onEditSavedLocation?: (savedLocationId: Id, index: number) => void;
  /**
   * Callback when accurate location is toggled. `patch` carries a better pin
   * that came with the answer — the accuracy modal resolves a pasted Google
   * Maps link into new coordinates and address.
   */
  onAccurateLocationToggle?: (isAccurate: boolean, patch?: Partial<MapLocationData>) => void;
}

/**
 * LocationCard - Clean location card matching old implementation design
 * Uses MapContext for editing state and enable flags
 */
export function LocationCard({
  location,
  index,
  isOrigin,
  onCardClick,
  onEdit,
  onDone,
  onCancel,
  onSaveLocation,
  onEditSavedLocation,
  onAccurateLocationToggle,
}: LocationCardProps) {
  const ctx = useMapContext();
  const { t } = useTranslation();

  // Get editing state and enable flags from context
  const isEditing = ctx.editingIndex === index;
  const hasBeenEdited = ctx.isLocationEdited(index);
  const enableEdit =
    ctx.mode !== 'view' && (ctx.mode === 'edit' || ctx.mode === 'add_new_location');
  const enableSavingPlaces = ctx.mode !== 'view' && ctx.props.locationsConfig?.enableSavingPlaces;
  const enableAccurateLocationToggle =
    ctx.mode !== 'view' && ctx.props.locationsConfig?.enableAccurateLocationToggle;
  // The host commits (its own Save button), so the card shows no ✓ / ✕.
  const autoCommitEdits = !!ctx.props.locationsConfig?.autoCommitEdits;

  const isAccurateLocation =
    location.isAccurateLocation !== undefined
      ? location.isAccurateLocation
      : location.savedLocation
      ? true
      : false;

  const savedLocation = location.savedLocation;
  const isSavedLocation = Boolean(savedLocation?._id);
  const clientName = savedLocation?.client?.name;
  const isOwnBranch = savedLocation?.isOwnBranch;
  const address = location.fullAddress || location.address?.fullAddress || '';
  const cityName = location.city || location.address?.city?.name || '';
  const countryName = location.address?.country?.name || '';

  // State for accuracy modal
  const [showAccuracyModal, setShowAccuracyModal] = useState(false);
  // Track if "Save Location" was clicked (to call onSaveLocation after modal confirmation)
  const [shouldSaveLocation, setShouldSaveLocation] = useState(false);

  // Reusable Save Location button handler
  const handleSaveLocationClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!onSaveLocation) return;

      // If editing and location is inaccurate, show accuracy modal first
      if (
        isEditing &&
        (location.isAccurateLocation === false || location.isAccurateLocation === undefined)
      ) {
        setShouldSaveLocation(true);
        setShowAccuracyModal(true);
      } else {
        // Location is accurate or not editing, save directly
        onSaveLocation();
        if (isEditing) {
          onDone?.();
        }
      }
    },
    [isEditing, location.isAccurateLocation, onSaveLocation, onDone],
  );

  // Determine hover border glow based on accuracy and saved status (subtle)
  const getHoverBorderGlow = () => {
    if (isEditing) return '';
    if (hasBeenEdited) return 'hover:border-purple-300/30';
    if (isSavedLocation) return 'hover:border-blue-300/20';
    if (isAccurateLocation) return 'hover:border-green-300/20';
    if (location.isAccurateLocation === false) return 'hover:border-orange-300/25';
    return 'hover:border-primary/15';
  };

  return (
    <div
      className={`group relative p-3 bg-white/20 backdrop-blur-md rounded-lg transition-all duration-300 ease-in-out cursor-pointer ${
        isEditing
          ? 'border-2 border-orange-500 shadow-lg'
          : `border border-gray-200/50 shadow-sm hover:shadow-lg hover:bg-white/30 ${getHoverBorderGlow()} hover:-translate-y-0.5`
      } ${hasBeenEdited && !isEditing ? 'border-purple-400' : ''}`}
      onClick={onCardClick}
      onMouseDown={e => e.stopPropagation()}>
      <div className={`flex flex-col`}>
        {/* Top Section: Content and Edit Icon - no bottom margin; bottom section uses mt-2 when present to avoid white space when no actions */}
        <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0 text-start">
            {/* 1. City Name */}
            <div
              className="flex items-center gap-2 mb-1.5 justify-start flex-wrap">
              <p className="text-sm font-semibold text-gray-900 truncate">{cityName}</p>
              {/* Edited indicator - prominent status badge with icon */}
              {hasBeenEdited && !isEditing && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-purple-600 text-white shadow-sm animate-pulse flex-shrink-0"
                  title={t('map.locationEdited', 'Location has been edited')}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    <path d="m15 5 4 4" />
                  </svg>
                  {t('map.edited', 'Edited')}
                </span>
              )}
            </div>

            {/* 2. Address */}
            {address && (
              <p className="text-[11px] font-[500] leading-tight text-gray-500 mb-2.5 line-clamp-2">
                {address}
              </p>
            )}

            {/* 3. Saved Location Name */}
            {savedLocation?.name && isSavedLocation && (
              <div className="mb-2.5">
                <SavedLocationDisplay savedLocationName={savedLocation.name} />
              </div>
            )}

            {/* 4. Third Party Customer Name (if available) */}
            {isSavedLocation && !isOwnBranch && clientName && (
              <ClientDisplay clientName={clientName} className="mb-2.5" />
            )}

            {/* 5. Contact Info */}
            {savedLocation?.contacts && savedLocation.contacts.length > 0 && (
              <div className="mb-2.5 min-h-[14px]">
                <ContactsTooltipDisplay
                  contacts={savedLocation.contacts}
                  headerTitle={t('contact.contacts', 'Contacts')}
                  primaryLabel={t('contact.primary', 'Primary')}
                  defaultContactLabel={t('contact.defaultContact', 'Default Contact')}
                />
              </div>
            )}
          </div>

          {/* Right Side: Edit Icon Only - Always visible */}
          {!isEditing && enableEdit && onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="bg-white border rounded-full flex-shrink-0 h-8 w-8 transition-opacity duration-200"
              onClick={e => {
                e.stopPropagation();
                onEdit();
              }}>
              <Edit2 className="w-4 h-4 text-gray-500" />
            </Button>
          )}

          {/* Done and Cancel Buttons - shown when editing */}
          {isEditing && !autoCommitEdits && (
            <div className="flex items-center gap-2 flex-wrap">
              {onCancel && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 flex-shrink-0"
                  onClick={e => {
                    e.stopPropagation();
                    onCancel();
                  }}>
                  <X className="w-4 h-4" />
                </Button>
              )}
              {/* Done Button - type="button" so it does not submit parent form */}
              {onDone && (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="h-8 px-3 flex-shrink-0 bg-green-600 hover:bg-green-700 text-white"
                  onClick={e => {
                    e.stopPropagation();
                    // Only show accuracy modal when toggle is enabled; otherwise exit immediately
                    if (
                      enableAccurateLocationToggle &&
                      location.isAccurateLocation === false
                    ) {
                      setShouldSaveLocation(false);
                      setShowAccuracyModal(true);
                    } else {
                      onDone();
                    }
                  }}>
                  <Check className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Bottom Section: only render when there is something to show (avoids white space for saved locations when not editing) */}
        {(() => {
          const showAccurateButton =
            !isEditing &&
            !isSavedLocation &&
            location.isAccurateLocation === false &&
            enableAccurateLocationToggle &&
            onEdit;
          const showSaveButton =
            !isEditing &&
            !isSavedLocation &&
            location.isAccurateLocation !== false &&
            location.isAccurateLocation !== undefined &&
            enableSavingPlaces &&
            onSaveLocation;
          const showEditSavedLocationButton =
            isEditing &&
            isSavedLocation &&
            enableSavingPlaces &&
            onEditSavedLocation &&
            savedLocation?._id;
          if (!showAccurateButton && !showSaveButton && !showEditSavedLocationButton) return null;
          return (
            <div className="flex items-center gap-2 mt-2">
              {showAccurateButton && (
                <Button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  variant="secondary"
                  size="sm"
                  className="h-7 px-2 text-xs bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200">
                  <AlertTriangle className="w-4 h-4 me-1" />
                  {t('location.accurateLocationButton', 'Accurate Location')}
                </Button>
              )}
              {showSaveButton && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-3 text-xs border-blue-600 text-blue-600 hover:bg-blue-50 hover:border-blue-700 hover:text-blue-700"
                  onClick={handleSaveLocationClick}>
                  <LocationAddIcon />
                  {t('location.saveLocationButton', 'Save Location')}
                </Button>
              )}
              {showEditSavedLocationButton && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-3 text-xs border-gray-500 text-gray-700 hover:bg-gray-100 hover:border-gray-600 hover:text-gray-800"
                  onClick={e => {
                    e.stopPropagation();
                    onEditSavedLocation(savedLocation!._id!, index);
                  }}>
                  <PencilIcon className="size-4" />
                  {t('location:buttons.edit_saved_location', 'Edit saved location')}
                </Button>
              )}
            </div>
          );
        })()}
      </div>

      {/* Location Accuracy Modal - shown when Done is clicked on inaccurate location */}
      <LocationAccuracyModal
        location={location}
        isOpen={showAccuracyModal}
        onClose={() => {
          setShowAccuracyModal(false);
        }}
        onConfirm={({ isAccurate, patch }) => {
          // Update location accuracy, plus the resolved pin when the user
          // supplied a Google Maps link.
          if (onAccurateLocationToggle) {
            onAccurateLocationToggle(isAccurate, patch);
          }
          // If "Save Location" was clicked, call onSaveLocation
          if (shouldSaveLocation && onSaveLocation && !isSavedLocation) {
            onSaveLocation();
          }
          // Close modal and exit edit mode
          setShowAccuracyModal(false);
          setShouldSaveLocation(false);
          onDone?.();
        }}
      />
    </div>
  );
}
